import { CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ButtonData {
  text: string;
  url: string;
  variant: "primary" | "outline";
}

interface BenefitsSectionProps {
  title: string;
  subtitle: string;
  content: string; // items separated by "|"
  buttons?: ButtonData[];
  backgroundColor?: string;
}

export function BenefitsSection({ title, subtitle, content, buttons = [], backgroundColor }: BenefitsSectionProps) {
  const items = content.split("|").map(s => s.trim()).filter(Boolean);

  return (
    <section
      className="py-16 sm:py-24 px-4"
      style={{ backgroundColor: backgroundColor || undefined }}
    >
      <div className="max-w-6xl mx-auto space-y-12">
        <div className="text-center space-y-4 max-w-2xl mx-auto">
          {subtitle && (
            <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-primary bg-primary/10 px-4 py-1.5 rounded-full">
              {subtitle}
            </span>
          )}
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-foreground tracking-tight">{title}</h2>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((item, i) => (
            <div
              key={i}
              className="group flex items-start gap-4 p-5 rounded-xl bg-background border border-border/60 shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-0.5 hover:border-primary/30"
            >
              <div className="shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <CheckCircle className="h-5 w-5 text-primary" />
              </div>
              <span className="text-foreground font-medium text-sm sm:text-base leading-snug pt-2">{item}</span>
            </div>
          ))}
        </div>

        {buttons.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            {buttons.map((btn, i) => (
              <Button
                key={i}
                variant={btn.variant === "outline" ? "outline" : "default"}
                size="lg"
                className="font-semibold transition-all duration-300 hover:-translate-y-0.5"
                asChild
              >
                <a href={btn.url} target="_blank" rel="noopener noreferrer">{btn.text}</a>
              </Button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
